import React, { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Fade,
  IconButton,
  Modal,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Block as BlockIcon,
  Cancel as CancelIcon,
  Close as CloseIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Schedule as ScheduleIcon,
} from "@mui/icons-material";
import { alpha, styled } from "@mui/material/styles";
import useMapViewState from "../../../hooks/useMapViewState";
import { getCancelledOrderDetail } from "../../../api/order";
import { dispatchAddAlert } from "../../../utils/alerts";

const StyledModalRoot = styled(Box)(
  ({ theme, mapViewState, width, height, overflow }) => {
    const isMapHidden = mapViewState === "hidden";

    const baseStyles = {
      position: "absolute",
      backgroundColor: theme.palette.background.paper,
      borderRadius: theme.spacing(2),
      boxShadow: theme.shadows[24],
      padding: 0,
      maxHeight: "90vh",
      display: "flex",
      flexDirection: "column",
      outline: "none",
      "&:focus": {
        outline: "none",
      },
    };

    if (isMapHidden) {
      return {
        ...baseStyles,
        top: "50%",
        left: "25%", // Position within the first column
        transform: "translate(-50%, -50%)",
        width: width || "80%",
        maxWidth: "600px",
        height: height || "auto",
        maxHeight: "80vh",
        overflow: overflow || "auto",
        [theme.breakpoints.down("md")]: {
          left: "50%",
          width: "90%",
          maxWidth: "90%",
        },
      };
    }

    return {
      ...baseStyles,
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      width: width || "90%",
      maxWidth: "800px",
      height: height || "auto",
      maxHeight: "90vh",
      overflow: overflow || "auto",
      [theme.breakpoints.down("md")]: {
        width: "90%",
        maxWidth: "90%",
      },
    };
  }
);

export default function OrderCancellationModal({
  show,
  orderId,
  orderIndex,
  customerName,
  cancellationReason,
  onClose,
  onAddToBlacklist,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { mapViewState } = useMapViewState();

  const [orderDetails, setOrderDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const loadOrderDetails = async () => {
      if (!show || !orderId) return;

      setIsLoading(true);
      try {
        const { data } = await getCancelledOrderDetail(orderId);
        setOrderDetails(data);
      } catch (error) {
        dispatchAddAlert({
          message: "Erro ao carregar detalhes do pedido cancelado",
          type: "error",
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadOrderDetails();
  }, [show, orderId]);

  const handleClose = () => {
    setOrderDetails(null);
    if (onClose) {
      onClose();
    }
  };

  const handleAddToBlacklist = () => {
    if (onAddToBlacklist) {
      onAddToBlacklist();
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "Data não disponível";
    return new Date(dateString).toLocaleString("pt-BR");
  };

  const formatAddress = (address) => {
    if (!address) return "Endereço não disponível";

    const addressParts = [
      address.street,
      address.house_number,
      address.neighborhood,
      address.city,
      address.uf,
      address.cep,
    ].filter(Boolean);

    return addressParts.join(", ");
  };

  const renderCustomerInfo = () => {
    if (!orderDetails?.customer) return null;

    const customer = orderDetails.customer;

    return (
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent sx={{ p: 2 }}>
          <Box display="flex" alignItems="center" mb={1}>
            <Avatar sx={{ bgcolor: theme.palette.primary.light, mr: 1 }}>
              <PersonIcon />
            </Avatar>
            <Typography variant="h6" color="primary">
              Informações do Cliente
            </Typography>
          </Box>

          <Stack spacing={1}>
            <Typography variant="body2">
              <strong>Nome:</strong> {customer.name || "Não informado"}
            </Typography>

            {customer.email && (
              <Box display="flex" alignItems="center">
                <EmailIcon
                  fontSize="small"
                  sx={{ mr: 1, color: theme.palette.grey[600] }}
                />
                <Typography variant="body2">{customer.email}</Typography>
              </Box>
            )}

            {customer.phonenmbr && (
              <Box display="flex" alignItems="center">
                <PhoneIcon
                  fontSize="small"
                  sx={{ mr: 1, color: theme.palette.grey[600] }}
                />
                <Typography variant="body2">{customer.phonenmbr}</Typography>
              </Box>
            )}

            {customer.cpf && (
              <Typography variant="body2">
                <strong>CPF:</strong> {customer.cpf}
              </Typography>
            )}

            {customer.is_blacklisted && (
              <Chip
                label="Cliente Não Confiável"
                color="error"
                size="small"
                sx={{ alignSelf: "flex-start" }}
              />
            )}
          </Stack>
        </CardContent>
      </Card>
    );
  };

  const renderAddressInfo = () => {
    if (!orderDetails?.address) return null;

    return (
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent sx={{ p: 2 }}>
          <Box display="flex" alignItems="center" mb={1}>
            <Avatar sx={{ bgcolor: theme.palette.secondary.light, mr: 1 }}>
              <LocationIcon />
            </Avatar>
            <Typography variant="h6" color="secondary">
              Endereço de Entrega
            </Typography>
          </Box>

          <Typography variant="body2">
            {formatAddress(orderDetails.address)}
          </Typography>

          {orderDetails.address.address_complement && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              <strong>Complemento:</strong>{" "}
              {orderDetails.address.address_complement}
            </Typography>
          )}

          {orderDetails.address.address_reference && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              <strong>Referência:</strong>{" "}
              {orderDetails.address.address_reference}
            </Typography>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderCancellationEvents = () => {
    if (!orderDetails?.events?.length) return null;

    return (
      <Card variant="outlined">
        <CardContent sx={{ p: 2 }}>
          <Box display="flex" alignItems="center" mb={2}>
            <Avatar sx={{ bgcolor: theme.palette.error.light, mr: 1 }}>
              <ScheduleIcon />
            </Avatar>
            <Typography variant="h6" color="error">
              Histórico de Cancelamento
            </Typography>
          </Box>

          <Stack spacing={2}>
            {orderDetails.events.map((event, index) => (
              <Box
                key={event.id || index}
                sx={{
                  p: 2,
                  border: `1px solid ${theme.palette.grey[200]}`,
                  borderRadius: 1,
                  backgroundColor: theme.palette.grey[50],
                }}
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  mb={1}
                >
                  <Typography variant="body2" fontWeight="600">
                    Evento #{index + 1}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatDateTime(event.createdat)}
                  </Typography>
                </Box>

                {event.reason && (
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Motivo:</strong> {event.reason}
                  </Typography>
                )}

                {event.cancel_code && (
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Código:</strong> {event.cancel_code}
                  </Typography>
                )}

                {event.cancel_stage && (
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Estágio:</strong> {event.cancel_stage}
                  </Typography>
                )}

                {event.origin && (
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Origem:</strong> {event.origin}
                  </Typography>
                )}

                {event.created_by_info && (
                  <Typography variant="body2" color="text.secondary">
                    <strong>Cancelado por:</strong>{" "}
                    {event.created_by_info.first_name}{" "}
                    {event.created_by_info.last_name} (
                    {event.created_by_info.username})
                  </Typography>
                )}
              </Box>
            ))}
          </Stack>
        </CardContent>
      </Card>
    );
  };

  return (
    <Modal
      open={show}
      onClose={handleClose}
      aria-labelledby="order-cancellation-title"
      aria-describedby="order-cancellation-description"
      closeAfterTransition
      sx={{
        zIndex: 9999,
        border: "none",
        "&:focus-visible": {
          border: "none",
        },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: alpha("#000", 0.5),
        },
      }}
    >
      <Fade in={show}>
        <StyledModalRoot
          mapViewState={mapViewState}
          width={isMobile ? "90%" : "60%"}
          height={isMobile ? "90%" : "80%"}
          overflow="auto"
        >
          <Box display="flex" flexDirection="column" height="100%" p={3}>
            <IconButton
              onClick={handleClose}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: theme.palette.grey[500],
                zIndex: 1,
              }}
              size="small"
            >
              <CloseIcon />
            </IconButton>

            <Box textAlign="center" mb={3}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  backgroundColor: theme.palette.error.light,
                  mb: 2,
                  mx: "auto",
                }}
              >
                <CancelIcon
                  sx={{
                    fontSize: 40,
                    color: theme.palette.error.main,
                  }}
                />
              </Box>

              <Typography
                id="order-cancellation-title"
                variant="h5"
                component="h2"
                fontWeight="600"
                color="text.primary"
                gutterBottom
              >
                PEDIDO CANCELADO
              </Typography>

              {orderIndex && (
                <Typography variant="body1" color="text.secondary">
                  Pedido #{orderIndex} foi cancelado
                </Typography>
              )}
            </Box>

            <Divider sx={{ mb: 3 }} />

            {isLoading ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                py={4}
              >
                <CircularProgress />
                <Typography variant="body2" sx={{ ml: 2 }}>
                  Carregando detalhes do pedido...
                </Typography>
              </Box>
            ) : (
              <Box sx={{ flexGrow: 1, overflow: "auto", mb: 3 }}>
                {renderCustomerInfo()}
                {renderAddressInfo()}
                {renderCancellationEvents()}
              </Box>
            )}

            <Stack spacing={2} sx={{ mt: "auto" }}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<BlockIcon />}
                onClick={handleAddToBlacklist}
                disabled={isLoading}
              >
                Adicionar à Lista de Não Confiáveis
              </Button>

              <Button variant="contained" fullWidth onClick={handleClose}>
                Fechar
              </Button>
            </Stack>
          </Box>
        </StyledModalRoot>
      </Fade>
    </Modal>
  );
}
